import { useState, useEffect } from 'react';
import ApiService from '../../services/api';
import HtmlEmailEditor from '../HtmlEmailEditor';
import Modal from '../ui/Modal';

const EmailModal = ({ 
  isOpen, 
  onClose, 
  document, 
  documentType, 
  client, 
  companySettings,
  onEmailSent 
}) => {
  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientName: '',
    subject: '',
    message: '',
    includeAttachment: true
  });
  
  const [useDefaultRecipient, setUseDefaultRecipient] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialize form data when modal opens or document changes
  useEffect(() => {
    if (isOpen && document && client) {
      const defaultSubject = documentType === 'invoice' 
        ? `Invoice #${document.number} from ${companySettings?.name || 'Your Company'}`
        : `Quote #${document.number} from ${companySettings?.name || 'Your Company'}`;

      const defaultMessage = generateDefaultHtmlMessage(document, documentType, client, companySettings);

      // Determine if we should default to client recipient
      const hasClientEmail = client.email && client.email.trim() !== '';
      
      setFormData({
        recipientEmail: hasClientEmail ? client.email : '',
        recipientName: hasClientEmail ? client.name || '' : '',
        subject: defaultSubject,
        message: defaultMessage,
        includeAttachment: true
      });
      
      setUseDefaultRecipient(hasClientEmail);
      setError('');
      setSuccess('');
    }
  }, [isOpen, document, client, documentType, companySettings]);

  // Dates are calendar dates stored at UTC midnight; local getters shift them a
  // day back west of UTC. DD/MM/YYYY to match the invoices.
  const formatDocumentDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return 'N/A';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
  };

  // Only the prose: the API wraps this in the branded template, which supplies
  // the logo, amount, line items, payment details and company footer.
  const generateDefaultHtmlMessage = (doc, type, clientData, settings) => {
    const name = clientData.contactPerson || clientData.name;

    if (type === 'invoice') {
      return `<p>Dear <strong>${name}</strong>,</p>
<p>Please find attached invoice <strong>${doc.number}</strong>.</p>
<p>Payment can be made using the details below. If you have any questions about this invoice, please don't hesitate to contact us.</p>
<p>Thank you for your business!</p>`;
    }

    return `<p>Dear <strong>${name}</strong>,</p>
<p>Thank you for your interest in our services. Please find attached quote <strong>${doc.number}</strong> for your consideration.</p>
<p>If you would like to proceed, or have any questions, just reply to this email. We look forward to working with you!</p>`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMessageChange = (value) => {
    setFormData(prev => ({
      ...prev,
      message: value
    }));
  };

  const handleRecipientToggle = (useDefault) => {
    setUseDefaultRecipient(useDefault);
    
    if (useDefault && client) {
      // Reset to client data
      setFormData(prev => ({
        ...prev,
        recipientEmail: client.email || '',
        recipientName: client.name || ''
      }));
    } else {
      // Clear recipient fields for custom input
      setFormData(prev => ({
        ...prev,
        recipientEmail: '',
        recipientName: ''
      }));
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!formData.recipientEmail) {
      setError('Recipient email is required');
      return;
    }

    if (!formData.subject) {
      setError('Subject is required');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (documentType === 'invoice') {
        await ApiService.sendInvoiceEmail(document._id, formData);
      } else {
        await ApiService.sendQuoteEmail(document._id, formData);
      }
      
      setSuccess(`${documentType === 'invoice' ? 'Invoice' : 'Quote'} email sent successfully!`);
      
      // Call parent callback
      if (onEmailSent) {
        onEmailSent(document, formData.recipientEmail);
      }
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Email send error:', error);
      setError(error.message || 'Failed to send email');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const hasClientEmail = client?.email && client.email.trim() !== '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          <i className="fas fa-envelope mr-2"></i>
          Send {documentType === 'invoice' ? 'Invoice' : 'Quote'} Email
        </>
      }
      size="xlarge"
      closeOnOverlay={!isLoading}
      footer={
        <div className="flex gap-3">
          <button 
            type="button" 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          
          <button 
            type="submit" 
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || !companySettings?.smtpHost}
            onClick={handleSend}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane mr-2"></i>
                Send Email
              </>
            )}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSend}>
        <div className="p-0">
              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <i className="fas fa-exclamation-circle text-red-400"></i>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm text-red-800">
                        {error}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {success && (
                <div className="rounded-md bg-green-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <i className="fas fa-check-circle text-green-400"></i>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm text-green-800">
                        {success}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Document Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {documentType === 'invoice' ? 'Invoice' : 'Quote'} #{document?.number}
                    </div>
                    <div className="text-sm text-gray-500">
                      Date: {formatDocumentDate(document?.date)}
                    </div>
                  </div>
                  <div className="md:text-right">
                    <div className="font-semibold text-gray-900">To: {client?.name}</div>
                    <div className="text-sm text-gray-500">{client?.email}</div>
                  </div>
                </div>
              </div>

              {/* Recipient Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">Recipient</label>
                
                {hasClientEmail ? (
                  <div className="space-y-3">
                    {/* Default Recipient Option */}
                    <div className="flex items-center">
                      <input
                        id="default-recipient"
                        name="recipient-type"
                        type="radio"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={useDefaultRecipient}
                        onChange={() => handleRecipientToggle(true)}
                        disabled={isLoading}
                      />
                      <label htmlFor="default-recipient" className="ml-2 block text-sm text-gray-900">
                        Send to default contacts
                      </label>
                    </div>

                    {/* Custom Recipient Option */}
                    <div className="flex items-center">
                      <input
                        id="custom-recipient"
                        name="recipient-type"
                        type="radio"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={!useDefaultRecipient}
                        onChange={() => handleRecipientToggle(false)}
                        disabled={isLoading}
                      />
                      <label htmlFor="custom-recipient" className="ml-2 block text-sm text-gray-900">
                        Send to different recipient
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 rounded-md p-3 mb-3">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <i className="fas fa-exclamation-triangle text-yellow-400"></i>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm text-yellow-800">
                          No email address found for this client. Please enter recipient details below.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Email Form - Only show when not using default recipient */}
              {(!useDefaultRecipient || !hasClientEmail) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email *</label>
                    <input
                      type="email"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      placeholder="Enter recipient email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      name="recipientName"
                      value={formData.recipientName}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      placeholder="Enter recipient name"
                    />
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <HtmlEmailEditor
                  value={formData.message}
                  onChange={handleMessageChange}
                  disabled={isLoading}
                  rows={10}
                  placeholder="Write your message — branding and invoice details are added automatically..."
                  showPreview={true}
                />
                <div className="mt-1 text-sm text-gray-500">
                  Write the message only — it is wrapped in the branded template with your logo, the amount, line items, payment details and footer.
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center">
                  <input
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    type="checkbox"
                    name="includeAttachment"
                    checked={formData.includeAttachment}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Include PDF attachment
                  </label>
                </div>
              </div>

              {/* Email Settings Warning */}
              {!companySettings?.smtpHost && (
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <i className="fas fa-exclamation-triangle text-yellow-400"></i>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm text-yellow-800">
                        <strong>Email not configured:</strong> Please configure your email settings in the Settings page before sending emails.
                      </div>
                    </div>
                  </div>
                </div>
              )}
        </div>
      </form>
    </Modal>
  );
};

export default EmailModal;